import { useMemo, type RefObject } from 'react';
import { Tearsheet } from '@carbon/ibm-products';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { PlainLink } from 'components/link';
import type {
  PackageContext,
  RunOverrides,
} from 'lib/evaluation-case/package-context';
import {
  SCENARIO_DOMAINS,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import { CaseCoverageMap } from 'pages/evaluation-cases/case-editor/coverage-map/case-coverage-map';
import {
  archivedScenarioIds,
  mapResponseToForm,
} from 'pages/evaluation-cases/case-editor/utils';
import type { ScenarioChoice } from 'pages/evaluation-cases/case-editor/types';
import { useEvaluationCaseDetail } from './hooks';
import styles from './case-coverage-tearsheet.module.scss';

type RunPicks = Partial<Record<ScenarioDomainKey, number>>;

/** No run overrides: one stable object, so the map's picks memo doesn't rerun every render. */
const NO_OVERRIDES: RunPicks = Object.freeze({});

/** The pick a run overrides a domain the case leaves empty with: only its id is read. */
const EMPTY_CHOICE: ScenarioChoice = {
  id: 0,
  name: '',
  areaTags: [],
  areaTagsMoreCount: 0,
  areaTagsAppliesToAll: false,
};

export interface CaseCoverageTearsheetProps {
  open: boolean;
  onClose: () => void;
  /** The icon that opened it: focus returns there on close. */
  launcherButtonRef: RefObject<HTMLButtonElement>;
  caseId: number;
  caseName: string;
  /** The case page's path, for the "Open case" link. */
  caseHref: string;
  packageName: string;
  /** This package and the run's Type Curve scenario: the map counts and draws its slots. */
  packageContext: PackageContext;
  /**
   * The run's "This run only" picks (`{ domain: scenario id }`): the map draws these in place of
   * the case's own, as the run would use them.
   */
  overrides?: RunPicks;
  /** The overrides a Calculate of this eval would run with: the map counts the slots under them. */
  runOverrides?: RunOverrides | null;
}

/**
 * The case's coverage map for this package, from the eval page's case panel: the case page's own
 * map (CaseCoverageMap) and the case as the case page loads it, given this package, so it draws
 * the red stack and green and the package's valued and not-valued slots, with the same legend.
 * The body mounts only while open, so nothing loads until it opens. The map fills the content
 * area and re-fits as it settles (the open animation, then any window resize).
 *
 * @param {CaseCoverageTearsheetProps} props - Open state, the case and the package.
 * @return {JSX.Element} The tearsheet.
 */
export function CaseCoverageTearsheet({
  open,
  onClose,
  launcherButtonRef,
  caseId,
  caseName,
  caseHref,
  packageName,
  packageContext,
  overrides = NO_OVERRIDES,
  runOverrides = null,
}: CaseCoverageTearsheetProps): JSX.Element {
  return (
    <Tearsheet
      open={open}
      onClose={onClose}
      className={styles.tearsheet}
      label="Case coverage"
      title={packageName ? `${caseName} · ${packageName}` : caseName}
      description={
        <PlainLink href={caseHref} openInNewTab>
          Open case page
        </PlainLink>
      }
      hasCloseIcon
      closeIconDescription="Close"
      launcherButtonRef={launcherButtonRef}
    >
      {open && (
        <CaseCoverageBody
          caseId={caseId}
          packageContext={packageContext}
          overrides={overrides}
          runOverrides={runOverrides}
        />
      )}
    </Tearsheet>
  );
}

/**
 * The map, once the case has loaded: the case's picks with the run's overrides applied.
 *
 * @param {object} props - The case id, the package context and the run's overrides.
 * @param {number} props.caseId - The case.
 * @param {PackageContext} props.packageContext - This package and Type Curve scenario.
 * @param {object} props.overrides - The run's `{ domain: scenario id }` picks.
 * @param {RunOverrides | null} props.runOverrides - The overrides a Calculate would run with.
 * @return {JSX.Element} The map, or its loading or error state.
 */
function CaseCoverageBody({
  caseId,
  packageContext,
  overrides,
  runOverrides,
}: {
  caseId: number;
  packageContext: PackageContext;
  overrides: RunPicks;
  runOverrides: RunOverrides | null;
}): JSX.Element {
  // The eval page's own read of the case (one cache entry for both pages): a cache hit when the
  // eval page already holds it.
  const { data, isError, error } = useEvaluationCaseDetail(caseId);
  const scenarios = useMemo(() => {
    if (!data) return null;
    const picks = { ...mapResponseToForm(data).scenarios };
    // The map reads a pick's id (its footprint, the preview): an override swaps the id only.
    SCENARIO_DOMAINS.forEach(({ key }) => {
      const id = overrides[key];
      if (typeof id === 'number' && id !== picks[key]?.id) {
        picks[key] = { ...(picks[key] ?? EMPTY_CHOICE), id };
      }
    });
    return picks;
  }, [data, overrides]);
  const archivedIds = useMemo(() => archivedScenarioIds(data ?? null), [data]);

  if (isError) {
    return (
      <InlineNotification
        kind="error"
        title="Couldn't load the case"
        subtitle={error?.message}
        hideCloseButton
        lowContrast
      />
    );
  }
  if (!data || !scenarios) {
    return <InlineLoading description="Loading the case..." />;
  }
  return (
    <div className={styles.body}>
      <CaseCoverageMap
        scenarios={scenarios}
        archivedScenarioIds={archivedIds}
        coverageBbox={data.coverageBbox ?? null}
        packageContext={packageContext}
        runOverrides={runOverrides}
        fill
      />
    </div>
  );
}
